import { authService } from '../services/authService';

/**
 * Acesso único ao estado persistido do Welcome Wizard.
 *
 * Contexto do bug (onda 3): o wizard grava seu progresso inteiro (themeId, step,
 * etc.) numa chave GLOBAL de localStorage (`agendario_welcome_session`), sem vínculo
 * com a conta. No mesmo navegador, ao trocar de conta (excluir + recriar, ou dois
 * usuários distintos), o wizard novo restaurava cegamente o estado da conta anterior
 * — incluindo o tema — fazendo o Welcome aparecer "com tema escurо que ninguém
 * escolheu" e pulando telas já preenchidas por outra conta.
 *
 * Correção: carimbamos `userId` dentro do payload e, na leitura, uma sessão que NÃO
 * pertence ao usuário atual é tratada como inexistente (null). A chave permanece a
 * mesma (não quebra nada), mas o conteúdo cruzado nunca é aplicado.
 */

export const WELCOME_SESSION_KEY = 'agendario_welcome_session';

function currentUserId() {
  try {
    return authService.getUserInfo()?.userId ?? null;
  } catch {
    return null;
  }
}

/**
 * Lê a sessão do Welcome — SÓ se pertencer ao usuário logado atual.
 *
 * - Se não há usuário identificável ainda (`uid == null`), devolve o que estiver lá
 *   (não temos como distinguir — o gate posterior reconcilia).
 * - Se há usuário e o carimbo não bate (inclui sessões legadas SEM carimbo, de antes
 *   deste fix), devolve `null` — impede vazamento de estado entre contas.
 *
 * @returns {object|null}
 */
export function readWelcomeSession() {
  try {
    const raw = localStorage.getItem(WELCOME_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const uid = currentUserId();
    if (uid != null && parsed.userId !== uid) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persiste a sessão do Welcome carimbando sempre o `userId` do usuário atual.
 * @param {object} state
 */
export function writeWelcomeSession(state) {
  try {
    const uid = currentUserId();
    localStorage.setItem(
      WELCOME_SESSION_KEY,
      JSON.stringify({ ...state, userId: uid ?? state?.userId ?? null }),
    );
  } catch {
    /* localStorage indisponível — ignora (wizard opera em memória) */
  }
}

/** Remove a sessão do Welcome. */
export function clearWelcomeSession() {
  try {
    localStorage.removeItem(WELCOME_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/** True se a sessão do usuário atual está marcada como concluída. */
export function isWelcomeCompleted() {
  const s = readWelcomeSession();
  return !!s?.completedAt;
}
